import { Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { VisitService } from '../../core/visit/visit.service';
import { FooterComponent } from '../../layout/footer/footer.component';
import { HeaderComponent } from '../../layout/header/header.component';
import { FieldErrorTooltipComponent } from '../../shared/components/field-error-tooltip/field-error-tooltip.component';
import { FooterNavComponent } from '../../shared/components/footer-nav/footer-nav.component';
import { TouchCheckboxComponent } from '../../shared/components/touch-checkbox/touch-checkbox.component';
import {
  VirtualKeyboardComponent,
  VirtualKeyboardKey,
} from '../../shared/components/virtual-keyboard/virtual-keyboard.component';

export type IdentificationField = 'lastName' | 'firstName' | 'company';

@Component({
  selector: 'app-visitor-identification',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    FooterNavComponent,
    VirtualKeyboardComponent,
    FieldErrorTooltipComponent,
    TouchCheckboxComponent,
  ],
  templateUrl: './visitor-identification.component.html',
  styleUrl: './visitor-identification.component.scss',
})
export class VisitorIdentificationComponent {
  private readonly router       = inject(Router);
  private readonly visitService = inject(VisitService);

  readonly lastName = signal('');
  readonly firstName = signal('');
  readonly companyName = signal('');
  readonly focusedField = signal<IdentificationField>('lastName');
  readonly showNameError = signal(false);

  /** « Je ne représente pas de société » — dispense de renseigner la société. */
  readonly noCompany = signal(false);
  readonly showCompanyError = signal(false);

  /**
   * Saisie société conservée pendant que la case est cochée.
   *
   * Le champ est vidé à l'affichage mais pas détruit : sur un clavier virtuel
   * sans correction, reperdre « CONSTRUCTEL RESEAUX SUD-OUEST » sur un appui
   * accidentel est une punition disproportionnée.
   */
  private companyBackup = '';

  focusField(field: IdentificationField): void {
    if (field === 'company') {
      this.focusCompany();
      return;
    }
    this.focusedField.set(field);
    this.showNameError.set(false);
  }

  /**
   * Le champ société reste tapable même quand la case est cochée : un élément
   * réellement `disabled` n'émet rien, et le visiteur qui tape dessus croirait
   * la borne figée. Taper le champ vaut « en fait, j'ai une société » —
   * l'intention explicite l'emporte sur l'état précédent.
   */
  focusCompany(): void {
    if (this.noCompany()) {
      this.setNoCompany(false);
    }
    this.focusedField.set('company');
    this.showCompanyError.set(false);
  }

  toggleNoCompany(): void {
    this.setNoCompany(!this.noCompany());
  }

  onKeyPress(key: VirtualKeyboardKey): void {
    this.appendToFocusedField(key);
  }

  onBackspace(): void {
    const field = this.focusedField();
    if (field === 'lastName') {
      this.lastName.update((value) => value.slice(0, -1));
      return;
    }
    if (field === 'firstName') {
      this.firstName.update((value) => value.slice(0, -1));
      return;
    }
    this.companyName.update((value) => value.slice(0, -1));
  }

  onSpace(): void {
    this.appendToFocusedField(' ');
  }

  onValidate(): void {
    // Les deux erreurs sont évaluées ensemble : le visiteur voit d'un coup tout
    // ce qui lui manque, plutôt que de buter deux fois sur « Valider ».
    const hasNameError = !this.lastName().trim() || !this.firstName().trim();
    const hasCompanyError = !this.companyName().trim() && !this.noCompany();

    this.showNameError.set(hasNameError);
    this.showCompanyError.set(hasCompanyError);

    if (hasNameError || hasCompanyError) {
      return;
    }

    this.visitService.updateVisitor({
      lastName:  this.lastName().trim(),
      firstName: this.firstName().trim(),
      // Case cochée ou champ vide donnent le même résultat : pas de société.
      // Rien d'autre n'est transmis — la case est purement front (SPECS §5.1).
      company:   this.companyName().trim() || undefined,
    });
    this.visitService.updateFlow({ entryMethod: 'manual' });
    this.visitService.trackEvent('identification_completed');
    void this.router.navigate(['/appointment-choice']);
  }

  hasFieldError(field: IdentificationField): boolean {
    if (field === 'company') {
      return this.showCompanyError();
    }
    return this.showNameError();
  }

  displayValue(field: IdentificationField): string {
    if (field === 'lastName') {
      return this.lastName();
    }
    if (field === 'firstName') {
      return this.firstName();
    }
    return this.companyName();
  }

  /** Coche/décoche en sauvegardant puis restaurant la saisie société. */
  private setNoCompany(checked: boolean): void {
    if (checked === this.noCompany()) {
      return;
    }

    if (checked) {
      this.companyBackup = this.companyName();
      this.companyName.set('');
      this.noCompany.set(true);
      this.showCompanyError.set(false);
      if (this.focusedField() === 'company') {
        this.focusedField.set('lastName');
      }
      return;
    }

    this.noCompany.set(false);
    this.companyName.set(this.companyBackup);
  }

  private appendToFocusedField(value: string): void {
    const field = this.focusedField();
    if (field === 'lastName') {
      this.lastName.update((current) => current + value);
      return;
    }
    if (field === 'firstName') {
      this.firstName.update((current) => current + value);
      return;
    }
    this.companyName.update((current) => current + value);
    this.showCompanyError.set(false);
  }
}
